import React, { useMemo, useEffect } from "react";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";
import { AnalyticsButton } from "./AnalyticsButton";
import calculatorParameters from "../../../data/calculator_parameters.json";

export const AnalyticsHighlights = ({
  optimizationResults,
  selectedRecommendedTruck,
  setSelectedRecommendedTruck,
}) => {
  const getTruckInfo = useMemo(() => {
    const truckInfo = {};
    calculatorParameters.ElectricTruckModelInfo.forEach(truck => {
      truckInfo[truck.id] = {
        id: truck.id,
        name: truck.title,
        image: truck.imgUrl.startsWith('/') ? `${window.location.origin}${truck.imgUrl}` : truck.imgUrl,
        link: truck.oemLink,
        price: truck.msrp,
        batteryCapacity: truck.batteryCap,
        range: truck.operationRange,
        class: truck.class,
      };
    });
    return truckInfo;
  }, []);

  const recommendedTruckModels = useMemo(() => {
    const models = {};
    const routes = optimizationResults?.data?.operation?.routes;
    if (!routes) return {};

    Object.values(routes).forEach(route => {
      if (route.selected_model_id && route.is_electric === true) {
        if (!models[route.selected_model_id]) {
          models[route.selected_model_id] = [route.route_name];
        } else {
          models[route.selected_model_id].push(route.route_name);
        }
      }
    });
    return models;
  }, [optimizationResults]);

  const truckInfo = getTruckInfo;
  const selectedRecommendedTruckInfo = selectedRecommendedTruck ? truckInfo[selectedRecommendedTruck] : null;

  useEffect(() => {
    if (optimizationResults && Object.keys(recommendedTruckModels).length > 0) {
      if (!selectedRecommendedTruck || !recommendedTruckModels[selectedRecommendedTruck]) {
        const firstTruckId = Object.keys(recommendedTruckModels)[0];
        setSelectedRecommendedTruck(firstTruckId);
      }
    }
  }, [optimizationResults, recommendedTruckModels, selectedRecommendedTruck, setSelectedRecommendedTruck]);

  const electricRoutesCount = Object.values(optimizationResults?.data?.operation?.routes || {}).filter(route => route.is_electric === true).length;
  const savings = optimizationResults?.data?.finance?.tco_analysis?.financial_metrics?.total_cost_of_ownership?.savings || 0;
  const zeroElectricWithSavings = electricRoutesCount === 0 && savings >= 0.5;

  const AnalyticsCard = ({ children }) => (
    <div className="bg-white p-6 font-nuntio rounded-lg shadow-md text-center flex flex-col justify-center">{children}</div>
  );

  const BigNumber = ({ children }) => (
    <p className="text-4xl lg:text-5xl font-bold text-gray-800 my-2 font-nunito">{children}</p>
  );

  const CardText = ({ children }) => (
    <p className="text-base text-gray-600 font-nunito">{children}</p>
  );

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnalyticsCard>
          <CardText>Over a <span className="font-bold text-blue-600">10</span> year period, you can save up to</CardText>
          <BigNumber>
            ${savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </BigNumber>
          <CardText>with our recommendations.</CardText>

          {zeroElectricWithSavings && (
            <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 text-left text-sm">
              <p className="font-bold text-blue-800">Note about these savings:</p>
              <p className="text-gray-700">
                Although we don't recommend electrifying your routes, your depots can benefit from battery arbitrage. If this is not your intended result, please turn off battery settings in the depot section.
              </p>
            </div>
          )}
        </AnalyticsCard>

        <AnalyticsCard>
          <CardText>Your cost per mile will decrease by</CardText>
          <BigNumber>
            ${optimizationResults.data.finance.tco_analysis.financial_metrics.tco_per_mile.savings.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
          </BigNumber>
          <CardText>
            from <span className="font-bold text-red-500">${optimizationResults.data.finance.tco_analysis.financial_metrics.tco_per_mile.pure_diesel_fleet.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span> to <span className="font-bold text-green-500">${optimizationResults.data.finance.tco_analysis.financial_metrics.tco_per_mile.optimized_fleet.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span> per mile.
          </CardText>
        </AnalyticsCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnalyticsCard>
          {(() => {
            const totalRoutes = Object.keys(optimizationResults.data.operation.routes).length;
            return (
              <>
                <CardText>Out of your <span className="font-bold text-blue-600">{totalRoutes}</span> {totalRoutes === 1 ? "route" : "routes"}, we recommend</CardText>
                <BigNumber>{electricRoutesCount}</BigNumber>
                <CardText>{electricRoutesCount === 1 ? "route" : "routes"} to be electrified.</CardText>
              </>
            );
          })()}
        </AnalyticsCard>

        <AnalyticsCard>
          {(() => {
            const numDepots = Object.keys(optimizationResults.data.operation.depots).length;
            const numChargers = Object.values(optimizationResults.data.operation.depots).reduce((total, depot) => total + Object.values(depot.chargers).reduce((sum, num) => sum + num, 0), 0);
            return (
              <>
                <CardText>In your <span className="font-bold text-blue-600">{numDepots}</span> {numDepots === 1 ? "depot" : "depots"}, we recommend</CardText>
                <BigNumber>{numChargers}</BigNumber>
                <CardText>{numChargers === 1 ? "charger" : "chargers"} for your fleet.</CardText>
              </>
            );
          })()}
        </AnalyticsCard>

        <AnalyticsCard>
          {(() => {
            const utilizedStations = Object.values(optimizationResults.data.operation.charging_stations || {}).filter(station => station.is_utilized);
            return (
              <>
                <CardText>Your fleet will use</CardText>
                <BigNumber>{utilizedStations.length}</BigNumber>
                <CardText>public {utilizedStations.length === 1 ? "station" : "stations"}.</CardText>
              </>
            );
          })()}
        </AnalyticsCard>

        <AnalyticsCard>
          <CardText>See more details in the sections below.</CardText>
        </AnalyticsCard>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        {Object.keys(recommendedTruckModels).length > 0 ? (
          <>
            <p className="text-xl font-semibold font-nunito text-gray-800 mb-4">Recommended truck(s) for your fleet:</p>
            
            {['Heavy Duty', 'Medium Duty', 'Light Duty'].map(dutyClass => {
              const trucksInClass = Object.keys(recommendedTruckModels).filter(model => truckInfo[model]?.class === dutyClass);
              if (trucksInClass.length === 0) return null;

              return (
                <div key={dutyClass} className="mb-6">
                  <h3 className="text-lg font-semibold font-nunito text-gray-700 mb-3 border-b pb-2">{dutyClass}</h3>
                  <div className="flex flex-wrap gap-4">
                    {trucksInClass.map(modelId => {
                      const truck = truckInfo[modelId];
                      const numRoutes = recommendedTruckModels[modelId].length;
                      return (
                        <AnalyticsButton
                          key={modelId}
                          label={`${truck ? truck.name : `Model ${modelId}`} x ${numRoutes}`}
                          handler={() => setSelectedRecommendedTruck(modelId)}
                          isSelected={selectedRecommendedTruck === modelId}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {selectedRecommendedTruckInfo && (
              <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-1">
                  <img src={selectedRecommendedTruckInfo.image} alt={selectedRecommendedTruckInfo.name} className="w-full h-auto object-contain rounded-lg shadow-lg" />
                </div>
                <div className="md:col-span-2 text-sm text-gray-700">
                  <div className="flex justify-between items-start">
                    <h4 className="text-2xl font-bold font-nunito text-gray-800 mb-2">{selectedRecommendedTruckInfo.name}</h4>
                    <a href={selectedRecommendedTruckInfo.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 transition flex items-center gap-2 font-nunito">
                      OEM Link <FaArrowUpRightFromSquare size={12} />
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-gray-50 p-3 rounded-md"><strong className="font-nunito">MSRP:</strong> <span className="font-nunito">${selectedRecommendedTruckInfo.price.toLocaleString()}</span></div>
                    <div className="bg-gray-50 p-3 rounded-md"><strong className="font-nunito">Battery:</strong> <span className="font-nunito">{selectedRecommendedTruckInfo.batteryCapacity} kWh</span></div>
                    <div className="bg-gray-50 p-3 rounded-md"><strong className="font-nunito">Range:</strong> <span className="font-nunito">{selectedRecommendedTruckInfo.range} miles</span></div>
                    <div className="bg-gray-50 p-3 rounded-md"><strong className="font-nunito">Class:</strong> <span className="font-nunito">{selectedRecommendedTruckInfo.class}</span></div>
                  </div>
                  <div className="mt-4 bg-gray-50 p-3 rounded-md">
                    <p className="font-semibold font-nunito">Assigned to {recommendedTruckModels[selectedRecommendedTruck].length} route(s):</p>
                    <p className="text-xs italic text-gray-600 font-nunito">{recommendedTruckModels[selectedRecommendedTruck].join(", ")}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 text-lg">No electric trucks were recommended for this analysis.</p>
          </div>
        )}
      </div>
    </div>
  );
};
